import type { LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface NavProps {
	isCollapsed: boolean;
	links: {
		title: string;
		label?: string;
		icon: LucideIcon;
		variant: "default" | "ghost";
		onSelect?: () => void;
	}[];
}

export function Nav({ links, isCollapsed }: NavProps) {
	return (
		<div
			data-collapsed={isCollapsed}
			className="group flex flex-col gap-4 py-2 data-[collapsed=true]:py-2"
		>
			<nav className="grid gap-1 px-2 group-[[data-collapsed=true]]:justify-center group-[[data-collapsed=true]]:px-2">
				{links.map((link) => {
					const Icon = link.icon;
					if (isCollapsed) {
						return (
							<Tooltip key={link.title}>
								<TooltipTrigger
									render={
										<Button
											variant={link.variant}
											size="icon"
											onClick={link.onSelect}
											className={cn(
												"size-9",
												link.variant === "default" &&
													"dark:bg-muted dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-white",
											)}
										>
											<Icon />
											<span className="sr-only">{link.title}</span>
										</Button>
									}
								/>
								<TooltipContent
									side="right"
									className="flex items-center gap-4"
								>
									{link.title}
									{link.label && (
										<span className="ml-auto text-muted-foreground">
											{link.label}
										</span>
									)}
								</TooltipContent>
							</Tooltip>
						);
					}
					return (
						<Button
							key={link.title}
							variant={link.variant}
							size="sm"
							onClick={link.onSelect}
							className={cn(
								"justify-start",
								link.variant === "default" &&
									"dark:bg-muted dark:text-white dark:hover:bg-muted dark:hover:text-white",
							)}
						>
							<Icon data-icon="inline-start" />
							{link.title}
							{link.label && (
								<span
									className={cn(
										"ml-auto",
										link.variant === "default" &&
											"text-background dark:text-white",
									)}
								>
									{link.label}
								</span>
							)}
						</Button>
					);
				})}
			</nav>
		</div>
	);
}
